import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createAccountScopedStorage, setCurrentAccountId } from '@/store/account/zustand-account-storage';
import { ChecklistItem, OnboardingFlowState, OnboardingFlowStore } from '../types/use-onboarding-flow-store-types';
import {
  OnboardingBusinessType,
  OnboardingDrawerStep,
  OnboardingPlatform,
} from '../types/use-onboarding-flow-store-enums';
import * as Logger from '@/lib/monitoring/logger';

const initialState: OnboardingFlowState = {
  businessType: null,
  platform: null,
  isShopifyAppCustomer: false,
  dataSourceConnected: false,
  costsConfigured: false,
  trackingInstalled: false,
  eventsConfigured: false,
  firstEventReceived: false,
  dataSources: {
    facebookAds: false,
    googleAds: false,
    tiktokAds: false,
    stripe: false,
  },
  destinations: {
    metaCapi: false,
    googleAdsCapi: false,
    tiktokEventsApi: false,
    klaviyo: false,
  },
  dismissed: false,
  requiredCompletedAt: null,
  recommendedDismissed: false,
  timezoneConfirmed: false,
  activeDrawerStep: OnboardingDrawerStep.NONE,
};

const STORE_VERSION = 1;
const STORE_BASE_NAME = '@upstack/onboarding-flow-store';

let lastAppliedAccountId: string | null = null;
let hasAppliedAccountId = false;

export function setOnboardingStoreAccountId(accountId: string | null): void {
  if (hasAppliedAccountId && lastAppliedAccountId === accountId) {
    return;
  }

  hasAppliedAccountId = true;
  lastAppliedAccountId = accountId;

  setCurrentAccountId(accountId);

  if (accountId) {
    const storageKey = `${STORE_BASE_NAME}-${accountId}`;
    const storedData = localStorage.getItem(storageKey);

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed?.state) {
          useOnboardingFlowStore.setState({ ...initialState, ...parsed.state });
        } else {
          useOnboardingFlowStore.setState({ ...initialState });
        }
      } catch (error) {
        Logger.warn('Failed to parse onboarding flow state', { error });
        useOnboardingFlowStore.setState({ ...initialState });
      }
    } else {
      // Account key has no data — try migrating from the base key.
      const baseData = localStorage.getItem(STORE_BASE_NAME);
      if (baseData) {
        try {
          const parsed = JSON.parse(baseData);
          if (parsed?.state) {
            useOnboardingFlowStore.setState({ ...initialState, ...parsed.state });
            localStorage.setItem(storageKey, baseData);
            return;
          }
        } catch (error) {
          Logger.warn('Failed to migrate onboarding flow state', { error });
        }
      }
      useOnboardingFlowStore.setState({ ...initialState });
    }
  } else {
    useOnboardingFlowStore.setState({ ...initialState });
  }
}

const onboardingStorage = createJSONStorage<OnboardingFlowState>(() =>
  createAccountScopedStorage({
    baseName: STORE_BASE_NAME,
  })
);

export const useOnboardingFlowStore = create<OnboardingFlowStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setBusinessType: (businessType) => {
          set({ businessType }, false, 'SET_BUSINESS_TYPE');
        },

        setPlatform: (platform) => {
          set({ platform }, false, 'SET_PLATFORM');
        },

        setBusinessTypeAndPlatform: (businessType, platform) => {
          set({ businessType, platform }, false, 'SET_BUSINESS_TYPE_AND_PLATFORM');
        },

        setIsShopifyAppCustomer: (isShopifyAppCustomer) => {
          set({ isShopifyAppCustomer }, false, 'SET_IS_SHOPIFY_APP_CUSTOMER');
        },

        setDataSourceConnected: (connected) => {
          set({ dataSourceConnected: connected }, false, 'SET_DATA_SOURCE_CONNECTED');
        },

        setTrackingInstalled: (installed) => {
          set({ trackingInstalled: installed }, false, 'SET_TRACKING_INSTALLED');
        },

        setEventsConfigured: (configured) => {
          set({ eventsConfigured: configured }, false, 'SET_EVENTS_CONFIGURED');
        },

        setCostsConfigured: (configured) => {
          set({ costsConfigured: configured }, false, 'SET_COSTS_CONFIGURED');
        },

        setFirstEventReceived: (received) => {
          const currentState = get();
          const updates: Partial<OnboardingFlowState> = { firstEventReceived: received };

          // Check if all required steps are now complete using the helper
          if (received && !currentState.requiredCompletedAt) {
            const stateWithUpdate = { ...currentState, firstEventReceived: received };
            if (isRequiredComplete(stateWithUpdate)) {
              updates.requiredCompletedAt = new Date().toISOString();
            }
          }

          set(updates, false, 'SET_FIRST_EVENT_RECEIVED');
        },

        setDataSourceConnection: (source, connected) => {
          set(
            (state) => ({
              dataSources: {
                ...state.dataSources,
                [source]: connected,
              },
            }),
            false,
            'SET_DATA_SOURCE_CONNECTION'
          );
        },

        setDestinationConnection: (destination, connected) => {
          set(
            (state) => ({
              destinations: {
                ...state.destinations,
                [destination]: connected,
              },
            }),
            false,
            'SET_DESTINATION_CONNECTION'
          );
        },

        dismiss: () => {
          set({ dismissed: true }, false, 'DISMISS_ONBOARDING');
        },

        setTimezoneConfirmed: (confirmed) => {
          const currentState = get();
          const updates: Partial<OnboardingFlowState> = { timezoneConfirmed: confirmed };

          // If confirming timezone is the step that closes out onboarding
          // (e.g. first_event arrived earlier, as it often does), stamp the
          // completion timestamp — setFirstEventReceived can't do it when
          // timezoneConfirmed is still false at that moment.
          if (confirmed && !currentState.requiredCompletedAt) {
            const stateWithUpdate = { ...currentState, timezoneConfirmed: confirmed };
            if (isRequiredComplete(stateWithUpdate)) {
              updates.requiredCompletedAt = new Date().toISOString();
            }
          }

          set(updates, false, 'SET_TIMEZONE_CONFIRMED');
        },

        setActiveDrawerStep: (step) => {
          set({ activeDrawerStep: step }, false, 'SET_ACTIVE_DRAWER_STEP');
        },

        openDrawer: (step) => {
          set({ activeDrawerStep: step }, false, 'OPEN_DRAWER');
        },

        closeDrawer: () => {
          set({ activeDrawerStep: OnboardingDrawerStep.NONE }, false, 'CLOSE_DRAWER');
        },

        reset: () => {
          set({ ...initialState }, false, 'RESET_ONBOARDING_FLOW');
        },
      }),
      {
        name: STORE_BASE_NAME,
        version: STORE_VERSION,
        storage: onboardingStorage,
        migrate: (persistedState, _version) => {
          // Future migrations can be added here
          // if (version === 0) { ... migrate to version 1 ... }
          return persistedState as OnboardingFlowState;
        },
      }
    ),
    { enabled: process.env.NODE_ENV === 'development' }
  )
);

export function hasAnyDataSourceConnected(state: OnboardingFlowState): boolean {
  return (
    state.dataSources.facebookAds ||
    state.dataSources.googleAds ||
    state.dataSources.tiktokAds ||
    state.dataSources.stripe
  );
}

export function hasAnyDestinationConnected(state: OnboardingFlowState): boolean {
  return (
    state.destinations.metaCapi ||
    state.destinations.googleAdsCapi ||
    state.destinations.tiktokEventsApi ||
    state.destinations.klaviyo
  );
}

export function isShopifyOnboardingPath(state: OnboardingFlowState): boolean {
  return (
    state.businessType === OnboardingBusinessType.ECOMMERCE &&
    state.platform === OnboardingPlatform.SHOPIFY
  );
}

export function getRequiredChecklistItems(state: OnboardingFlowState): ChecklistItem[] {
  const items: ChecklistItem[] = [
    {
      id: 'business_type',
      label: 'Select your business type',
      completed: state.businessType !== null && state.platform !== null,
      actionLabel: 'Select',
    },
  ];

  if (!state.businessType || !state.platform) {
    return items;
  }

  // Non-Shopify flows don't have guided setup yet — until those are specced,
  // they get a single "set up with our team" step instead of the platform
  // branches below.
  if (!isShopifyOnboardingPath(state)) {
    items.push({
      id: 'contact_support',
      label: 'Set up with our team',
      description: 'Guided setup for your platform is coming soon',
      completed: false,
    });
    return items;
  }

  // Placed after tracking install in each branch so Shopify users have their
  // store's timezone synced to defaultTimezone by the time this step shows.
  const confirmTimezoneItem: ChecklistItem = {
    id: 'confirm_timezone',
    label: 'Confirm your timezone',
    description: 'Verify timezone for accurate reporting',
    completed: state.timezoneConfirmed,
    actionLabel: 'Confirm',
  };

  // E-commerce + Shopify path
  if (state.businessType === OnboardingBusinessType.ECOMMERCE && state.platform === OnboardingPlatform.SHOPIFY) {
    // Only show "Connect Shopify" step for Platform customers
    // Shopify app customers already have Shopify connected via their app install
    if (!state.isShopifyAppCustomer) {
      items.push({
        id: 'connect_shopify',
        label: 'Connect your Shopify store',
        description: 'We\'ll sync your orders and customer data',
        completed: state.dataSourceConnected,
        actionLabel: 'Connect',
      });
    }

    items.push(
      {
        id: 'install_tracking',
        label: 'Install tracking pixel',
        description: 'Theme extension AND checkout pixel',
        completed: state.trackingInstalled,
        actionLabel: 'Set up',
      },
      confirmTimezoneItem,
      {
        id: 'connect_data_source',
        label: 'Connect a data source',
        description: 'Import data from ad platforms into Upstack for reporting',
        completed: hasAnyDataSourceConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'connect_destination',
        label: 'Connect a pixel destination',
        description: 'Send pixel and CAPI events to ad platforms',
        completed: hasAnyDestinationConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'setup_costs',
        label: 'Set up costs',
        description: 'Shipping, gateway fees, and fixed costs for P&L',
        completed: state.costsConfigured,
        actionLabel: 'Set up',
      },
      {
        id: 'first_event',
        label: state.firstEventReceived ? 'First events received' : 'Waiting for first events...',
        description: 'Usually takes a few minutes after install',
        completed: state.firstEventReceived,
      }
    );
  }
  // E-commerce + WooCommerce
  else if (state.businessType === OnboardingBusinessType.ECOMMERCE && state.platform === OnboardingPlatform.WOOCOMMERCE) {
    items.push(
      {
        id: 'install_plugin',
        label: 'Install WooCommerce plugin',
        description: 'Download and activate in your WordPress admin',
        completed: state.dataSourceConnected,
        actionLabel: 'Install',
      },
      {
        id: 'connect_store',
        label: 'Connect your store',
        description: 'Enter your API credentials',
        completed: state.trackingInstalled,
        actionLabel: 'Set up',
      },
      confirmTimezoneItem,
      {
        id: 'connect_data_source',
        label: 'Connect a data source',
        description: 'Import data from ad platforms into Upstack for reporting',
        completed: hasAnyDataSourceConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'connect_destination',
        label: 'Connect a pixel destination',
        description: 'Send pixel and CAPI events to ad platforms',
        completed: hasAnyDestinationConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'first_event',
        label: state.firstEventReceived ? 'First events received' : 'Waiting for first events...',
        completed: state.firstEventReceived,
      }
    );
  }
  // E-commerce + Custom
  else if (state.businessType === OnboardingBusinessType.ECOMMERCE && state.platform === OnboardingPlatform.CUSTOM) {
    items.push(
      {
        id: 'install_tracking',
        label: 'Install tracking pixel',
        description: 'Add our JavaScript snippet to your site',
        completed: state.trackingInstalled,
        actionLabel: 'Set up',
      },
      {
        id: 'configure_events',
        label: 'Configure e-commerce events',
        description: 'Map your purchase, add-to-cart, checkout events',
        completed: state.eventsConfigured,
        actionLabel: 'Set up',
      },
      confirmTimezoneItem,
      {
        id: 'connect_data_source',
        label: 'Connect a data source',
        description: 'Import data from ad platforms into Upstack for reporting',
        completed: hasAnyDataSourceConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'connect_destination',
        label: 'Connect a pixel destination',
        description: 'Send pixel and CAPI events to ad platforms',
        completed: hasAnyDestinationConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'first_event',
        label: state.firstEventReceived ? 'First events received' : 'Waiting for first events...',
        completed: state.firstEventReceived,
      }
    );
  }
  // Lead Gen paths
  else if (state.businessType === OnboardingBusinessType.LEAD_GEN) {
    if (state.platform === OnboardingPlatform.WORDPRESS) {
      items.push(
        {
          id: 'install_plugin',
          label: 'Install WordPress plugin',
          description: 'One-click install from WordPress.org',
          completed: state.trackingInstalled,
          actionLabel: 'Install',
        }
      );
    } else {
      items.push(
        {
          id: 'install_tracking',
          label: 'Install tracking pixel',
          description: 'Add to your site\'s custom code section',
          completed: state.trackingInstalled,
          actionLabel: 'Set up',
        }
      );
    }

    items.push(
      {
        id: 'configure_events',
        label: 'Configure conversion events',
        description: 'Form submissions, signups, contact requests',
        completed: state.eventsConfigured,
        actionLabel: 'Set up',
      },
      confirmTimezoneItem,
      {
        id: 'connect_data_source',
        label: 'Connect a data source',
        description: 'Import data from ad platforms into Upstack for reporting',
        completed: hasAnyDataSourceConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'connect_destination',
        label: 'Connect a pixel destination',
        description: 'Send pixel and CAPI events to ad platforms',
        completed: hasAnyDestinationConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'first_event',
        label: state.firstEventReceived ? 'First events received' : 'Waiting for first events...',
        completed: state.firstEventReceived,
      }
    );
  }
  // SaaS paths
  else if (state.businessType === OnboardingBusinessType.SAAS) {
    if (state.platform === OnboardingPlatform.REACT) {
      items.push(
        {
          id: 'install_sdk',
          label: 'Install SDK',
          description: 'npm install @upstack/tracker',
          completed: state.trackingInstalled,
          actionLabel: 'Set up',
        },
        {
          id: 'initialize_tracking',
          label: 'Initialize tracking',
          description: 'Add initialization code to your app',
          completed: state.dataSourceConnected,
          actionLabel: 'Set up',
        }
      );
    } else {
      items.push(
        {
          id: 'install_tracking',
          label: 'Install tracking pixel',
          description: 'Add our JavaScript snippet to your site',
          completed: state.trackingInstalled,
          actionLabel: 'Set up',
        }
      );
    }

    items.push(
      {
        id: 'configure_events',
        label: 'Configure conversion events',
        description: 'Signup, subscription, trial start, etc.',
        completed: state.eventsConfigured,
        actionLabel: 'Set up',
      },
      confirmTimezoneItem,
      {
        id: 'connect_data_source',
        label: 'Connect a data source',
        description: 'Import data from ad platforms into Upstack for reporting',
        completed: hasAnyDataSourceConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'connect_destination',
        label: 'Connect a pixel destination',
        description: 'Send pixel and CAPI events to ad platforms',
        completed: hasAnyDestinationConnected(state),
        actionLabel: 'Connect',
      },
      {
        id: 'first_event',
        label: state.firstEventReceived ? 'First events received' : 'Waiting for first events...',
        completed: state.firstEventReceived,
      }
    );
  }

  return items;
}

export function getFirstSessionShopifyChecklistItems(state: OnboardingFlowState): ChecklistItem[] {
  return [
    {
      id: 'connect_shopify',
      label: 'Connect your Shopify store',
      description:
        'Bring orders and customer data into Upstack so your reports can start filling in.',
      completed: state.dataSourceConnected,
      actionLabel: 'Connect',
    },
    {
      id: 'install_tracking',
      label: 'Turn on Shopify tracking',
      description:
        'Complete two steps in Shopify so Upstack can capture visits, checkouts, and purchases.',
      completed: state.trackingInstalled,
      actionLabel: 'Set up',
    },
  ];
}

export function isFirstSessionShopifyComplete(state: OnboardingFlowState): boolean {
  return state.dataSourceConnected && state.trackingInstalled;
}

export function isRequiredComplete(state: OnboardingFlowState): boolean {
  const requiredItems = getRequiredChecklistItems(state);
  return requiredItems.length > 0 && requiredItems.every(item => item.completed);
}

const INDEPENDENT_CHECKLIST_ITEM_IDS = new Set([
  'confirm_timezone',
  'connect_data_source',
  'connect_destination',
  'setup_costs',
  'configure_events',
  'initialize_tracking',
]);

export function isIndependentChecklistItem(itemId: string): boolean {
  return INDEPENDENT_CHECKLIST_ITEM_IDS.has(itemId);
}

const CHECKLIST_ITEM_DRAWER_STEPS: Record<string, OnboardingDrawerStep> = {
  business_type: OnboardingDrawerStep.BUSINESS_TYPE,
  confirm_timezone: OnboardingDrawerStep.CONFIRM_TIMEZONE,
  connect_shopify: OnboardingDrawerStep.CONNECT_SHOPIFY,
  connect_store: OnboardingDrawerStep.CONNECT_SHOPIFY,
  install_tracking: OnboardingDrawerStep.INSTALL_TRACKING,
  install_plugin: OnboardingDrawerStep.INSTALL_TRACKING,
  install_sdk: OnboardingDrawerStep.INSTALL_TRACKING,
  configure_events: OnboardingDrawerStep.CONFIGURE_EVENTS,
  initialize_tracking: OnboardingDrawerStep.CONFIGURE_EVENTS,
  connect_data_source: OnboardingDrawerStep.SELECT_DATA_SOURCE,
  connect_destination: OnboardingDrawerStep.SELECT_DESTINATION,
  setup_costs: OnboardingDrawerStep.SETUP_COSTS,
  facebook_ads: OnboardingDrawerStep.CONNECT_FACEBOOK_ADS,
  google_ads: OnboardingDrawerStep.CONNECT_GOOGLE_ADS,
  tiktok_ads: OnboardingDrawerStep.CONNECT_TIKTOK_ADS,
  stripe: OnboardingDrawerStep.CONNECT_STRIPE,
  meta_capi: OnboardingDrawerStep.CONNECT_META_CAPI,
  google_capi: OnboardingDrawerStep.CONNECT_GOOGLE_CAPI,
  tiktok_events: OnboardingDrawerStep.CONNECT_TIKTOK_EVENTS,
  klaviyo: OnboardingDrawerStep.CONNECT_KLAVIYO,
};

export function getDrawerStepForChecklistItem(itemId: string): OnboardingDrawerStep | null {
  return CHECKLIST_ITEM_DRAWER_STEPS[itemId] ?? null;
}

export function useOnboardingFlowStateSnapshot(): OnboardingFlowState {
  return useOnboardingFlowStore(
    useShallow((s) => ({
      businessType: s.businessType,
      platform: s.platform,
      isShopifyAppCustomer: s.isShopifyAppCustomer,
      dataSourceConnected: s.dataSourceConnected,
      trackingInstalled: s.trackingInstalled,
      eventsConfigured: s.eventsConfigured,
      firstEventReceived: s.firstEventReceived,
      costsConfigured: s.costsConfigured,
      dataSources: s.dataSources,
      destinations: s.destinations,
      dismissed: s.dismissed,
      requiredCompletedAt: s.requiredCompletedAt,
      recommendedDismissed: s.recommendedDismissed,
      timezoneConfirmed: s.timezoneConfirmed,
      activeDrawerStep: OnboardingDrawerStep.NONE,
    }))
  );
}
